import { useState, useEffect, useRef } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import {
  Shield, Check, Loader2, AlertCircle, Upload, Users, Bell, Chrome,
  ArrowRight, Clock, Server, BarChart3, Settings, Zap
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const ACTIVATION_STEPS = {
  pro: {
    title: 'Welcome to Pro!',
    subtitle: 'Get the most from your Pro subscription in 3 steps:',
    steps: [
      { icon: Chrome, title: 'Install Monitor', desc: 'Add the Chrome extension for real-time tracking transparency.', cta: 'Install Extension', link: '/monitor' },
      { icon: Bell, title: 'Enable Doorbell Alerts', desc: 'Get notified when new AI agents or spikes are detected.', cta: 'Open Monitor', link: '/monitor' },
      { icon: Clock, title: 'Check 90-day History', desc: 'Your full 90-day and YTD timeline is now unlocked.', cta: 'View Timeline', link: '/monitor' },
    ],
  },
  teams: {
    title: 'Welcome to Teams!',
    subtitle: 'Set up your team in 3 steps:',
    steps: [
      { icon: Users, title: 'Invite Teammates', desc: 'Share access with up to 5 team members.', cta: 'Send Invites', link: 'mailto:support@agentcheck.com?subject=Team%20Setup' },
      { icon: Settings, title: 'Set Org Policies', desc: 'Configure default blocking rules for your team.', cta: 'Configure', link: '/monitor' },
      { icon: Bell, title: 'Enable Team Alerts', desc: 'Shared doorbell notifications for your organization.', cta: 'Set Up Alerts', link: '/monitor' },
    ],
  },
  teams_verify: {
    title: 'Welcome to Teams + Verify!',
    subtitle: 'Your team controls + server log intelligence is ready:',
    steps: [
      { icon: Upload, title: 'Upload Your First Log', desc: 'Nginx or Apache access log (.log, .txt, .gz). Results in minutes.', cta: 'Upload Now', link: '/verify' },
      { icon: Users, title: 'Invite Teammates', desc: 'Share Pro features with up to 5 team members.', cta: 'Send Invites', link: 'mailto:support@agentcheck.com?subject=Team%20Setup' },
      { icon: Bell, title: 'Enable Alerts', desc: 'Get email alerts when bot activity spikes.', cta: 'Configure', link: '/monitor' },
    ],
  },
  verify_merchant: {
    title: 'Welcome to Verify Merchant!',
    subtitle: 'See who\'s scraping your store in 3 steps:',
    steps: [
      { icon: Upload, title: 'Upload Your First Log', desc: 'Drag your Nginx/Apache access log. We support .log, .txt, and .gz.', cta: 'Upload Now', link: '/verify' },
      { icon: BarChart3, title: 'Review Bot Report', desc: 'See detected bots, top endpoints hit, and confidence scores.', cta: 'View Dashboard', link: '/verify' },
      { icon: Server, title: 'Share Verified Report', desc: 'Download the Verified Activity card for your team or stakeholders.', cta: 'Generate Card', link: '/verify' },
    ],
  },
  verify_plus: {
    title: 'Welcome to Verify Plus!',
    subtitle: 'Multi-domain bot intelligence is ready:',
    steps: [
      { icon: Upload, title: 'Upload Logs for 3 Domains', desc: 'Start with your highest-traffic store. Add more anytime.', cta: 'Upload Now', link: '/verify' },
      { icon: Bell, title: 'Set Up Alerts', desc: 'Get email alerts when bot activity spikes or new bot families appear.', cta: 'Configure Alerts', link: '/verify' },
      { icon: Clock, title: 'Schedule Weekly Reports', desc: '90-day retention means you can track trends over time.', cta: 'View Trends', link: '/verify' },
    ],
  },
  verify_ops: {
    title: 'Welcome to Commerce Ops!',
    subtitle: 'Enterprise bot intelligence is activated:',
    steps: [
      { icon: Upload, title: 'Upload or Connect Logs', desc: 'S3 direct uploads enabled. Process up to 500GB/month.', cta: 'Upload Now', link: '/verify' },
      { icon: Zap, title: 'Onboarding Call', desc: 'Your dedicated onboarding session — we\'ll help you set up.', cta: 'Book Call', link: 'mailto:support@agentcheck.com?subject=Commerce%20Ops%20Onboarding' },
      { icon: BarChart3, title: 'API Access', desc: 'Export reports programmatically. 180-day retention.', cta: 'View API Docs', link: '/verify' },
    ],
  },
};

function getActivation(planId) {
  if (!planId) return ACTIVATION_STEPS.pro;
  if (planId.startsWith('verify_ops')) return ACTIVATION_STEPS.verify_ops;
  if (planId.startsWith('verify_plus')) return ACTIVATION_STEPS.verify_plus;
  if (planId.startsWith('verify_merchant')) return ACTIVATION_STEPS.verify_merchant;
  if (planId.startsWith('teams_verify')) return ACTIVATION_STEPS.teams_verify;
  if (planId.startsWith('teams')) return ACTIVATION_STEPS.teams;
  return ACTIVATION_STEPS.pro;
}

export default function BillingSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session_id');
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [planId, setPlanId] = useState(null);
  const polledRef = useRef(false);

  useEffect(() => {
    if (!sessionId || polledRef.current) return;
    polledRef.current = true;

    let attempts = 0;

    async function poll() {
      try {
        const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/checkout/status/${sessionId}`);
        if (!res.ok) throw new Error('Failed');
        const data = await res.json();
        setPaymentData(data);

        // Try to get plan_id from transaction
        try {
          const txnRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/billing/checkout/status/${sessionId}`);
          const txn = await txnRes.json();
          if (txn.plan_id) setPlanId(txn.plan_id);
        } catch {}

        if (data.payment_status === 'paid') { setStatus('success'); return; }
        if (data.status === 'expired') { setStatus('expired'); return; }
        if (++attempts >= 8) { setStatus('timeout'); return; }
        setTimeout(poll, 2000);
      } catch (err) {
        console.warn('Payment poll error:', err);
        if (++attempts >= 8) { setStatus('error'); return; }
        setTimeout(poll, 2000);
      }
    }
    poll();
  }, [sessionId]);

  const activation = getActivation(planId);

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="border-b border-slate-100">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#007AFF] flex items-center justify-center"><Shield className="w-5 h-5 text-white" strokeWidth={2} /></div>
            <span className="text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>AgentCheck</span>
          </Link>
        </div>
      </header>

      <main className="flex-1 px-4 py-12">
        <div className="max-w-lg mx-auto" data-testid="billing-success-content">
          {status === 'checking' && (
            <div className="text-center py-16">
              <Loader2 className="w-12 h-12 text-[#007AFF] animate-spin mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>Confirming payment...</h1>
              <p className="text-slate-500">Please wait while we verify your payment.</p>
            </div>
          )}

          {status === 'success' && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-8 h-8 text-emerald-600" />
                </div>
                <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>{activation.title}</h1>
                <p className="text-slate-500">
                  Payment of ${paymentData?.amount_total ? (paymentData.amount_total / 100).toFixed(2) : '—'} {paymentData?.currency?.toUpperCase()} confirmed.
                </p>
              </div>

              {/* Activation steps */}
              <div className="space-y-4 mb-8" data-testid="activation-steps">
                <p className="text-sm font-medium text-slate-700">{activation.subtitle}</p>
                {activation.steps.map((step, i) => {
                  const Icon = step.icon;
                  return (
                    <div key={i} className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100" data-testid={`activation-step-${i}`}>
                      <div className="w-10 h-10 rounded-lg bg-[#007AFF]/10 flex items-center justify-center flex-shrink-0">
                        <Icon className="w-5 h-5 text-[#007AFF]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="w-5 h-5 rounded-full bg-slate-200 text-slate-600 text-xs font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                          <h3 className="text-sm font-semibold text-slate-900">{step.title}</h3>
                        </div>
                        <p className="text-xs text-slate-500 mb-2">{step.desc}</p>
                        {step.link.startsWith('mailto:') ? (
                          <a href={step.link}><Button variant="outline" size="sm" className="h-7 rounded-full text-xs gap-1"><ArrowRight className="w-3 h-3" />{step.cta}</Button></a>
                        ) : (
                          <Button variant="outline" size="sm" className="h-7 rounded-full text-xs gap-1" onClick={() => navigate(step.link)}><ArrowRight className="w-3 h-3" />{step.cta}</Button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="text-center">
                <Button className="bg-[#007AFF] hover:bg-[#0062CC] rounded-full gap-2" onClick={() => navigate(activation.steps[0].link.startsWith('mailto:') ? '/' : activation.steps[0].link)} data-testid="go-primary-btn">
                  <ArrowRight className="w-4 h-4" /> Get Started
                </Button>
              </div>
            </>
          )}

          {(status === 'expired' || status === 'error' || status === 'timeout') && (
            <div className="text-center py-16">
              <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Manrope' }}>
                {status === 'expired' ? 'Session expired' : 'Something went wrong'}
              </h1>
              <p className="text-slate-500 mb-6">
                {status === 'expired' ? 'Your checkout session has expired.' : 'We could not confirm your payment. If charged, contact support@agentcheck.com.'}
              </p>
              <Button variant="outline" className="rounded-full" onClick={() => navigate('/pricing')} data-testid="retry-pricing-btn">Back to pricing</Button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
